import { useCallback, useRef } from 'react'

import { externalAuthApi } from '../api'
import { ROUTES } from '../constants'
import { getAuthErrorMessage } from '../utils/errorMapper'
import { ERROR_MESSAGES } from '../utils/errorMessages'
import { type UseAuthBaseOptions, useAuthBase } from './useAuthBase'

interface UsePasswordRecoveryOptions extends UseAuthBaseOptions {
  onRecoverySent?: () => void
}

interface UsePasswordRecoveryReturn {
  isLoading: boolean
  error: string | undefined
  handleForgotPasswordSubmit: (email: string) => Promise<void>
  handleSetNewPasswordFromRecoveryToken: (token: string, password: string) => Promise<boolean>
}

export function usePasswordRecovery(
  options: UsePasswordRecoveryOptions = {}
): UsePasswordRecoveryReturn {
  const { onRecoverySent, ...baseOptions } = options
  const onRecoverySentRef = useRef(onRecoverySent)
  onRecoverySentRef.current = onRecoverySent

  const { isLoading, error, setError, navigateToCallback, handleAsyncOperation } =
    useAuthBase(baseOptions)

  const handleForgotPasswordSubmit = useCallback(
    async (emailValue: string) => {
      const result = await handleAsyncOperation(async () => {
        return await externalAuthApi.requestPasswordRecovery({ email: emailValue })
      })

      if (!result) {
        return
      }

      const { response, data } = result

      if (response.ok) {
        if (onRecoverySentRef.current) {
          onRecoverySentRef.current()
        } else {
          navigateToCallback(ROUTES.PASSWORD_RECOVERY.BEGIN, { sent: 'true' })
        }
      } else {
        const errorMessage =
          getAuthErrorMessage(response, data, 'password-recovery') ?? ERROR_MESSAGES.generic.default
        setError(errorMessage)
      }
    },
    [handleAsyncOperation, navigateToCallback, setError]
  )

  const handleSetNewPasswordFromRecoveryToken = useCallback(
    async (token: string, password: string): Promise<boolean> => {
      const result = await handleAsyncOperation(async () => {
        return await externalAuthApi.setNewPasswordFromRecoveryToken({ token, password })
      })

      if (!result) {
        return false
      }

      const { response, data } = result

      if (response.ok && data?.success) {
        return true
      }
      const errorMessage =
        getAuthErrorMessage(response, data, 'password-reset') ?? ERROR_MESSAGES.generic.default
      setError(errorMessage)
      return false
    },
    [handleAsyncOperation, setError]
  )

  return {
    isLoading,
    error,
    handleForgotPasswordSubmit,
    handleSetNewPasswordFromRecoveryToken,
  }
}
